import { portalText } from "../i18n/portal.js";
/**
 * What Do not call renders, and what the console knows about the public Opt out page
 * (P14-04).
 *
 * TWO THINGS THIS FILE IS CAREFUL ABOUT.
 *
 * **The opt-out page is not a console route.** It is unauthenticated, it lives on the public
 * site, and it is scoped to one business (AC-32). The console's only relationship with it is
 * the card on Do not call that shows the link and opens it — so what lives here is a URL
 * built from the public site's origin, the same way the Shop view's link is built in
 * `order-book-api.ts`. There is no console screen that submits an opt-out, because a console
 * screen submitting one would be an authenticated person opting somebody else out, which is
 * the "Add a number" button and a different thing entirely.
 *
 * **"Operators may add but never remove" is a claim about the STAFF console**, and this file
 * is the customer one. What it carries is the other side of that: `addedByOperatorName` is
 * present on a row so the customer can see that WeTalk added it, and `removable` is true on
 * those rows, because a customer may remove a suppression an operator added.
 */

/** The four filters the segmented control offers. `all` is the absence of a filter. */
export type DoNotCallFilterView = "all" | "said_on_call" | "opt_out_page" | "added_by_member";

/** How a number got onto the list. */
export type DoNotCallSourceView =
  | "said_on_call"
  | "opt_out_page"
  | "added_by_member"
  | "added_by_operator";

/** Whether the suppression covers the whole account or one agent. */
export type DoNotCallScopeView = "every_voice_agent" | "one_voice_agent";

export interface DoNotCallRowView {
  readonly doNotCallEntryId: string;
  /** Partially masked by the server for a Member or a Viewer; never masked here. */
  readonly e164: string;
  readonly displayName: string | null;
  /** What the person actually said, kept as evidence. `null` for the other sources. */
  readonly spokenWords: string | null;
  readonly source: DoNotCallSourceView;
  readonly scope: DoNotCallScopeView;
  /** "Every agent", or the agent's own name when the scope is one agent. */
  readonly scopeText: string;
  readonly whenText: string;
  /** Set only for `added_by_operator`, so the customer can see who did it. */
  readonly addedByOperatorName: string | null;
  /**
   * The person in the account who typed or imported it, so the row can say "Added by you" only
   * when it was you, and name anyone else (customer-portal review #256). `null` for the other
   * sources and for an API key.
   */
  readonly addedByPersonId: string | null;
  readonly addedByPersonName: string | null;
  /** The note given with "Add a number" (review #245); it lives in the audit entry. */
  readonly note: string | null;
  /** What the person wrote on the public opt-out page (review #246). */
  readonly optOutMessage: string | null;
  /** The conversation the refusal was heard on, which "Hear it" opens. */
  readonly conversationId: string | null;
  /**
   * The agent that conversation belongs to — the one that HEARD the refusal — so "Hear it" can
   * open that agent's History at the conversation. Not what the entry covers (`scope`): an
   * account-wide suppression covers every agent and was still heard by exactly one. `null`
   * whenever `conversationId` is.
   */
  readonly conversationVoiceAgentId: string | null;
}

export interface DoNotCallStatView {
  readonly code: "on_the_list" | "added_this_week" | "conversations_skipped" | "dialled_in_error";
  readonly label: string;
  readonly value: number;
  readonly note: string;
}

export interface DoNotCallPageView {
  readonly row: readonly DoNotCallRowView[];
  readonly nextCursor: string | null;
  readonly matchedCount: number;
  readonly stat: readonly DoNotCallStatView[];
}

export interface DoNotCallDraftView {
  readonly e164: string;
  readonly displayName: string | null;
  readonly scope: DoNotCallScopeView;
  /** Required when `scope` is `one_voice_agent`. */
  readonly voiceAgentId: string | null;
  readonly note: string | null;
}

/** Why one line of an imported file was not added (review #252). */
export type DoNotCallUnreadableProblemView = "missing_number" | "invalid_number" | "too_many_fields";

export interface DoNotCallUnreadableRowView {
  readonly line: number;
  readonly problem: DoNotCallUnreadableProblemView;
}

export interface DoNotCallImportView {
  readonly addedCount: number;
  readonly alreadyPresentCount: number;
  readonly unreadableCount: number;
  /** The first unreadable lines, by line and reason; `unreadableCount` counts them all. */
  readonly unreadableRow: readonly DoNotCallUnreadableRowView[];
}

/** What "Remove selected" did (review #254). */
export interface DoNotCallRemovalView {
  readonly removedCount: number;
  /** Ids that were no longer on the list — someone removed them first. */
  readonly notFoundCount: number;
}

/** One language's refusal phrases, as the "What the agent listens for" card lists them. */
export interface RefusalPhraseSetView {
  readonly languageCode: string;
  /** The language's own name, in that language, as the language pack supplies it. */
  readonly languageName: string;
  readonly phrase: readonly string[];
}

/**
 * The refusal card.
 *
 * `deadlineMs` is on the view because the sentence beside the phrases is a promise with a
 * number in it — "the number is added here within a second" — and a screen that hard-coded
 * "a second" beside a contract that said something else would be the first place the two
 * drifted apart.
 */
export interface RefusalPhraseCardView {
  readonly languageSet: readonly RefusalPhraseSetView[];
  readonly deadlineMs: number;
  readonly languagesCoveredCount: number;
  /** Languages the agent speaks that have no refusal phrases yet. Never silently empty. */
  readonly languagesMissing: readonly string[];
}

/**
 * The "Your public opt-out page" card.
 *
 * The URL is composed from the public site's origin and the business slug, so a console
 * pointed at a staging site shows a staging link rather than a production one it cannot open.
 */
export interface OptOutPageView {
  readonly businessSlug: string;
  readonly displayName: string;
  readonly publicUrl: string;
}

/** `wetalk.io/stop/roma-pizzeria`, from an origin and a slug, with no guessing. */
export function optOutPageUrl(publicSiteBaseUrl: string, businessSlug: string): string {
  return `${publicSiteBaseUrl.replace(/\/+$/, "")}/stop/${encodeURIComponent(businessSlug)}`;
}

const SOURCE_COPY = () => (new Map<string, string>([
  ["said_on_call", portalText("shell", "said_so_on_a_call_3192fa3c")],
  ["opt_out_page", portalText("shell", "opt_out_page_5ba7186b")],
  ["added_by_operator", portalText("shell", "added_by_wetalk_24ef39d7")],
]));

/**
 * The row's "How" tag. AMENDED 2026-09-27 (customer-portal review #256, Konstantina's decision):
 * a number typed in or imported reads "Added by you" only when the signed-in person added it; a
 * colleague's reads "Added by <name>", and one written with an API key "Added through the API".
 * Every account member used to see "Added by you" on everyone's entries.
 */
export function doNotCallSourceLabel(
  row: Pick<DoNotCallRowView, "source" | "addedByPersonId" | "addedByPersonName">,
  signedInPersonId: string | null,
): string {
  if (row.source !== "added_by_member") return SOURCE_COPY().get(row.source) ?? row.source;
  if (row.addedByPersonId === null) return portalText("shell", "added_through_the_api_84dcbb78");
  if (row.addedByPersonId === signedInPersonId) return portalText("shell", "added_by_you_9ce55c14");
  return row.addedByPersonName === null ? portalText("shell", "added_by_someone_in_your_account_f4df9575") : portalText("shell", "added_by_value_d1de5624", { v0: String(row.addedByPersonName) });
}

/** What "Covers" reads when the suppression is account-wide. */
export const EVERY_VOICE_AGENT_SCOPE = () => (portalText("shell", "every_agent_1de96134"));

/** The row action, which differs by source: a call has audio, a form submission does not. */
export function doNotCallEvidenceLabel(source: string): string {
  return source === "said_on_call" ? portalText("shell", "hear_it_f7d30058") : portalText("shell", "details_dc3decbb");
}

/**
 * What the Do-not-call page says about itself, above the table.
 *
 * It states both halves of the invariant, because half of it on its own reads as a limitation
 * rather than as a protection.
 */
export const DO_NOT_CALL_STANDING_NOTE =
  () => (portalText("shell", "anyone_who_tells_an_agent_to_stop_calling_is_added_here_on_d8151991"));
